import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toggleSubscriptionExpireModal as handleExpiryModalState } from "../Redux/Actions";
import { SubscriptionExpiryTooltip as Tooltip } from "../Components/SubscriptionExpiryTooltip";
import { RootState } from "../Redux/Reducers";
import {
  ECOMM,
  ECOMM_ELITE,
  ECOMM_PLUS,
  SUBSCRIPTION_FLAG,
  SUBSCRIPTION_ID,
  ECOMM_NEW_SUBSCRIPTIONS,
} from "../Constants/subscription";
import {
  calculateRemainingTime,
  checkMerchantSubscriptionType,
  checkMerchantNewEcommSubscriptionType,
} from "../Utils/common";
import { FeatureLockKeyType, FeatureStatusType, ISubscriptionExpiryTooltipProps } from "../Types";
import { isSubscriptionExpiring } from "../Utils/subscription";
import LocalStorageHelper from "../Utils/LocalStorageHelper";
import { ECOMM_RENEW_LATER } from "../Constants/localStorageKeys";
import moment from "moment";
import { isOwner } from "../Utils/isOwner";

interface ISelectorReturn {
  featureStatus: FeatureStatusType;
  subscriptionFlag: number;
  userSubscriptionDetails: any;
  upcomingEcomSubscription: any;
  subscriptionId: number;
}

/**
 * One stop shop for getting details & required comparison regarding subscriptions for a merchant.
 * @returns
 */
const warningVisiblityDays = 30;
let callbackRef: unknown;
const SubscriptionDetailsSuite = () => {
  const dispatch = useDispatch();
  const { subscriptionFlag, userSubscriptionDetails, featureStatus, upcomingEcomSubscription, subscriptionId } =
    useSelector(
      (state: RootState): ISelectorReturn => ({
        subscriptionFlag: state.storeReducer.store?.subscription_flag,
        userSubscriptionDetails: state.userReducer.userSubscriptionDetails,
        featureStatus: state.subscriptionReducer.featureLocks,
        upcomingEcomSubscription: state.themeReducer.subscriptionDetails.upcomingEcomSubscription,
        subscriptionId: state.themeReducer.subscriptionDetails?.subscription?.subscription_id,
      }),
    );

  let isEcommEssential = checkMerchantSubscriptionType(ECOMM);
  let isEcommPro = checkMerchantSubscriptionType(ECOMM_PLUS);
  const isEcommElite = checkMerchantSubscriptionType(ECOMM_ELITE);
  isEcommEssential = isEcommElite || isEcommPro || isEcommEssential;
  isEcommPro = isEcommElite || isEcommPro;
  // const callbackRef = useRef<unknown>(null);

  let isEssential = checkMerchantNewEcommSubscriptionType(ECOMM_NEW_SUBSCRIPTIONS.ESSENTIAL);
  let isPro = checkMerchantNewEcommSubscriptionType(ECOMM_NEW_SUBSCRIPTIONS.PRO);
  const isElite = checkMerchantNewEcommSubscriptionType(ECOMM_NEW_SUBSCRIPTIONS.ELITE);
  isEssential = isElite || isPro || isEssential;
  isPro = isElite || isPro;

  useEffect(() => {
    return () => {
      clearCallbackRef();
    };
  }, []);

  const clearCallbackRef = () => {
    callbackRef = null;
  };

  const toggleSubscriptionExpiryModal = (value: boolean) => {
    // clear the callback ref (that holds the feature flow) when the modal closes
    if (!value) {
      clearCallbackRef();
    }
    dispatch(handleExpiryModalState(!!value));
  };

  const getExpiryDetails = useCallback(() => {
    const calculatedTime = calculateRemainingTime(userSubscriptionDetails?.expiry, false);
    const remainingDays = calculatedTime.remainingDays;
    const remainingSeconds = calculatedTime.remainingSeconds;
    return {
      remainingDays,
      remainingSeconds,
    };
  }, [userSubscriptionDetails]);

  const subscriptionStatus = useMemo(() => {
    const expiryCalculation = calculateRemainingTime(userSubscriptionDetails?.expiry, false);
    const isSubscribed = subscriptionFlag === SUBSCRIPTION_FLAG.SUBSCRIBED;
    return {
      isNotSubscribed: subscriptionFlag === SUBSCRIPTION_FLAG.NOT_SUBSCRIBED,
      isSubscribed,
      isOldUser: subscriptionFlag === SUBSCRIPTION_FLAG.OLD_USER,
      isExpired: subscriptionFlag === SUBSCRIPTION_FLAG.EXPIRED,
      isExpiring: isSubscribed && expiryCalculation.remainingDays <= warningVisiblityDays,
      isFinalTwoDaysRemaining:
        isSubscribed && expiryCalculation.remainingDays <= 2 && expiryCalculation.remainingDays >= 0,
    } as const;
  }, [userSubscriptionDetails]);

  function auditExpiryModalVisibility() {
    if (subscriptionStatus.isNotSubscribed) {
      return false;
    }
    if (subscriptionStatus.isExpired) {
      return true;
    }
    if (!isOwner()) {
      return false;
    }
    if (!subscriptionStatus.isExpiring) {
      return false;
    }
    if (subscriptionStatus.isFinalTwoDaysRemaining) {
      return true;
    }
    const renewLaterTime = LocalStorageHelper.get(ECOMM_RENEW_LATER);
    if (!renewLaterTime) {
      return true;
    }
    return calculateRemainingTime(renewLaterTime, false).remainingSeconds < 0;
  }

  /** This function returns a curried function with some built-in statements where you can
   * inherently check whether to trigger expiry modal or not, if not then move along with current feature flow
   * Why created a curried function? because as the feature entry-points are built on pressing the CTA therefore it seemed
   * cleaner way to pass a callback function to the onPress prop
   */
  const checkSubscriptionExpiry =
    (callback: (...args: any[]) => unknown, additionalConditionToShowExpiryModal = true) =>
    (...args: any[]) => {
      // Check whether to open the expiry modal or move on with normal feature flow
      if (!upcomingEcomSubscription && auditExpiryModalVisibility() && additionalConditionToShowExpiryModal) {
        // Only save the funciton if the modal is going to open & the subscription hasn't expired yet
        if (callback) {
          callbackRef = callback.bind(null, ...args);
        }
        toggleSubscriptionExpiryModal(true);
        return;
      }
      callback?.(...args);
    };

  // This action's CTA gets hidden after the subscription has expired
  const renewLater = () => {
    // If the subscription is expiring & still behind last two days then save the later date when to check again the expiry modal visibility
    if (!subscriptionStatus.isFinalTwoDaysRemaining) {
      const laterDate = moment().add(warningVisiblityDays - 2, "days");
      LocalStorageHelper.add(ECOMM_RENEW_LATER, laterDate);
    }
    // execute the feature flow as normal
    if (typeof callbackRef === "function") {
      callbackRef();
    }
    // hide the expiry modal if visible
    toggleSubscriptionExpiryModal(false);
  };

  const isFeatureLocked = (featureName: FeatureLockKeyType) => {
    return !!featureStatus[featureName]?.isLocked;
  };

  const getFeatureStatusDetails = (featureName: FeatureLockKeyType) => {
    return featureStatus[featureName];
  };

  return {
    // Makes available all the required details regarding the subscription the user has purchased
    userSubscriptionDetails,

    // An object that returns the current status of whether subscription, whether it has been purchased or has expired.
    subscriptionStatus,

    // Mainly exported to be used when required as a dependency to observe change in flag
    subscriptionFlag,

    // Subscription Type
    isEcommPro,

    isEcommElite,

    isEcommEssential,

    // New Subcription Type (DD-5564)
    isEssential,

    isPro,

    isElite,

    // adding -1 check as the its source is currently unknown, will remove this check or add more details once more information is facilitated
    // neverSubscribedToOldFlow means that the merchant is never subscribed to the old ecomm flow
    neverSubscribedToOldFlow:
      subscriptionFlag === SUBSCRIPTION_FLAG.NOT_SUBSCRIBED ||
      subscriptionFlag === -1 ||
      subscriptionId === SUBSCRIPTION_ID.SUBSCRIPTION_ID_NEW_ECOMMERCE,

    isNewEcommFlow: subscriptionId === SUBSCRIPTION_ID.SUBSCRIPTION_ID_NEW_ECOMMERCE,

    featureStatus,

    checkMerchantSubscriptionType,

    canShowSubscriptionExpiryModal: auditExpiryModalVisibility(),

    isSubscriptionExpiring,

    // Returns remaining time the user has left in his subscription journey
    getExpiryDetails,

    renewLater,

    // Handler to show a warning modal (added in root) when subscription expires
    toggleSubscriptionExpiryModal,

    checkSubscriptionExpiry,

    getFeatureStatusDetails,

    isFeatureLocked,

    // Tooltip component to show in case the subscription expires
    SubscriptionExpiryTooltip: function ({ children, enableTooltip, ...props }: ISubscriptionExpiryTooltipProps) {
      const enable = enableTooltip ?? subscriptionStatus.isExpired;
      return (
        <Tooltip enableTooltip={enable} {...props} isEcommPro={isEcommPro} subscriptionStatus={subscriptionStatus}>
          {children}
        </Tooltip>
      );
    },
  };
};

export const useSubscriptionDetailsSuite = SubscriptionDetailsSuite;
